import cloneDeep from 'lodash/cloneDeep';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import reduce from 'lodash/reduce';
import LogRocket from 'logrocket';
import { Capability, InternalCapability } from '../data/account';
import Session from './Session';
import Util from './Util';

export default class Analytics {
	/**
	 * The global variable pendo is loaded via the script tag in <head> of index.html
	 */
	static _pendo = isObject(window.pendo) ? window.pendo : {};

	/**
	 * We only want Pendo to be initialized once. After the pendo is initialized,
	 * the subsequent calls to initialize will be to send identify event instead.
	 */
	static _isPendoVisitorInitialized = false;

	static _segmentAnalyticsAgent = null;
	static _pendingEvents = [];

	static agent = {
		segment: 'segment',
	};

	static metadata = null;

	/**
	 * Query params that carry credentials or raw identifiers (e.g. the
	 * password-reset link `?username=<user>&token=<reset credential>`).
	 * They must never reach a third-party analytics pipeline.
	 */
	static _sensitiveParamPattern = '([?&#])(token|username)=[^&#]*';

	/**
	 * Redact sensitive query param values from a URL, query string or referrer.
	 *
	 * @param {string} value
	 * @returns {string}
	 */
	static redactSensitiveParams = value =>
		typeof value === 'string'
			? // fresh regex per call: a shared /g instance carries lastIndex state
				value.replace(
					new RegExp(this._sensitiveParamPattern, 'gi'),
					'$1$2=redacted'
				)
			: value;

	/**
	 * Segment source middleware. Segment stamps every event (page, track, ...)
	 * with context.page.{url,search,referrer}, so redact them here regardless of
	 * what the caller passed.
	 */
	static _redactSourceMiddleware = ({ payload, next }) => {
		try {
			const obj = payload && payload.obj;
			const page = obj && obj.context && obj.context.page;

			[page, obj && obj.properties].forEach(target => {
				if (isObject(target)) {
					['url', 'search', 'referrer'].forEach(key => {
						if (key in target) {
							target[key] = this.redactSensitiveParams(target[key]);
						}
					});
				}
			});
		} finally {
			next(payload);
		}
	};

	/**
	 * Load Segment Agent
	 */
	static load = segmentKey => {
		if (window.analytics && isFunction(window.analytics.load)) {
			// DEV Note: analytics._writeKey is included in the JS snippet provided by Segment Install Page.
			window.analytics._writeKey = segmentKey;
			// Must be registered before load(); the snippet queues it until the
			// real agent is ready, so it is in place before the first page().
			if (isFunction(window.analytics.addSourceMiddleware)) {
				window.analytics.addSourceMiddleware(this._redactSourceMiddleware);
			}
			const cdnUrl = 'https://analyticscdn.stamps.com';
			const apiDomain = 'analyticsapi.stamps.com';
			window.analytics.load(
				{ writeKey: segmentKey, cdnUrl },
				{
					integrations: {
						'Segment.io': {
							apiHost: `${apiDomain}/v1`,
							protocol: 'https',
						},
					},
				},
				{ obfuscate: true }
			);
			window.analytics.page();
		}
	};

	static _setSegmentAnalyticsAgent = () => {
		this._segmentAnalyticsAgent = window.analytics;

		// Process any pending events
		while (this._pendingEvents.length > 0) {
			const { event, properties } = this._pendingEvents.shift();
			this.trackEvent(event, properties, this.agent.segment);
		}
	};

	/**
	 * Send an identify event and a meta event.
	 *
	 * @param {object} metadata The metadata object.
	 */
	static _identify = metadata => {
		const { visitor, account } = metadata || {};
		const { isSignedIn } = Session.getData();

		if (isSignedIn) {
			// Initialize/Identify Pendo User
			try {
				if (this._isPendoVisitorInitialized) {
					this._pendo.identify({
						visitor,
						account,
					});
				} else {
					this._pendo.initialize({
						visitor,
						account,
					});

					this._isPendoVisitorInitialized = true;
				}
			} catch (e) {
				console.log('Failed to Initialize/Identify Pendo');
			}
		}

		// Identify and Group for Segment Agent
		if (this._segmentAnalyticsAgent) {
			try {
				if (isSignedIn) {
					const visitorObjCpy = cloneDeep(visitor),
						accountObjCpy = cloneDeep(account),
						userId = visitor.id,
						custId = account.id;

					delete visitorObjCpy.id;
					delete accountObjCpy.id;

					this._segmentAnalyticsAgent.identify(userId, visitorObjCpy);
					this._segmentAnalyticsAgent.group(custId, accountObjCpy);
				} else {
					this._segmentAnalyticsAgent.identify(parseInt(Util.getVisitor()));
				}
			} catch (e) {
				console.log('Failed to Identify Segment Agent');
			}
		}
	};

	/**
	 * Send a Track event
	 *
	 * @param {Object} analyticsAgent
	 * @param {string} event Event Name
	 * @param {object} paramsObj Print Data
	 */
	static trackEvent = (event, properties, agent) => {
		try {
			if (agent === this.agent.segment) {
				if (this._segmentAnalyticsAgent) {
					this._segmentAnalyticsAgent.track(event, properties);
				} else {
					// Queue the event if analytics isn't ready
					this._pendingEvents.push({ event, properties });
				}
			}
		} catch (e) {
			console.log(`Failed to call ${agent} track API`);
		}
	};

	/**
	 * Send a Segment Tracking event
	 *
	 * @param {Object} segmentData
	 */
	static trackSegmentEvent = segmentData => {
		const { trackingData, eventType } = segmentData;
		const { settings } = Session.getData();
		trackingData.app_page = settings.Page;

		Analytics.trackEvent(eventType, trackingData, Analytics.agent.segment);
	};

	/**
	 * Send a Segment Tracking event with added check of LaunchDarkly flags to determine
	 * whether or not to log the field value
	 *
	 * @param {Object} segmentData
	 */
	static trackSegmentInputEvent = segmentData => {
		const { trackingData, eventType, fieldValue } = segmentData;
		const { settings } = Session.getData();
		trackingData.app_page = settings.Page;
		const segmentInputCapture = Session.getFeatureFlags(
			'segment-input-capture'
		);

		if (
			segmentInputCapture &&
			segmentInputCapture[trackingData.field_name_dataqa] === true
		) {
			trackingData.field_value = fieldValue;
		}

		Analytics.trackEvent(eventType, trackingData, Analytics.agent.segment);
	};

	/**
	 * Return an object containing visitor and account metadata. Each contains at least an id field.
	 *
	 * @param {object} sessionData User's session data.
	 * @param {object} externalData User's external data.
	 * @returns {object}
	 */
	static _buildMetadata = (sessionData, externalData) => {
		const { accountInfo, role, settings } = sessionData;
		const {
			accountId,
			capabilities,
			corporationId,
			customerId,
			userId,
			customerDetails = {},
			meterDetails = {},
			planDetails = {},
			internalAccountDetails = {},
		} = accountInfo;

		// retrieve the visitor id (one will be generated if undefined)
		const visitorId = Util.getVisitor();

		// retrieve user's email, name and username
		const { username } = isObject(customerDetails) ? customerDetails : {};

		// retrieve user's meter name
		// eslint-disable-next-line no-unused-vars
		const { meterAddress } = isObject(meterDetails) ? meterDetails : {};

		const capabilitiesMetadata = this._buildCapabilitiesMetadata(
			capabilities,
			internalAccountDetails?.internalCapabilities
		);

		// object to initialize or identify the visitor
		return {
			visitor: {
				id: userId, // Required if user is logged in
				username,
				role,
				webClientVisitor: visitorId,
				lastBuildVersion: settings.BuildVersion,
				...capabilitiesMetadata,
				...externalData,
			},
			account: {
				id: customerId, // Required if using Pendo Feedback
				corpID: corporationId,
				rateSetType: internalAccountDetails?.ratesetType,
				isSAS: isNumber(accountId) ? accountId !== 0 : false,
				planID: planDetails?.planId,
			},
		};
	};

	/**
	 * Return an object containing user's capabilities (and internalCapabilities).
	 *
	 * @param {object} capabilities
	 * @param {object} internalCapabilities
	 * @returns {object}
	 */
	static _buildCapabilitiesMetadata = (capabilities, internalCapabilities) => {
		// first process user's internal capabilities
		const capabilitiesMetadata = reduce(
			// get an array of all internal capabilities
			Object.values(InternalCapability),
			(_metadata, key) => {
				_metadata[`capability_${key}`] = internalCapabilities.includes(key);
				return _metadata;
			},
			{}
		);

		// continue processing capabilites with the previous result and returns the result
		return reduce(
			// get an array of all capabilities
			Object.values(Capability),
			(_metadata, key) => {
				_metadata[`capability_${key}`] = capabilities.includes(key);
				return _metadata;
			},
			capabilitiesMetadata
		);
	};

	/**
	 * The Pendo agent must be initialized to track usage and to deliver guides.
	 * Call this method whenever information about the vistor becomes available;
	 * once your users are logged in/authenticated.
	 *
	 * @param {object} sessionData User's session data.
	 * @param {object} externalData User's external data.
	 */
	static set = (sessionData = {}, externalData = {}) => {
		const { settings = {}, isSignedIn = false, accountInfo = {} } = sessionData;

		// Per WEBAPPS-32745, don't track anonymous visitors
		if (isSignedIn) {
			this.metadata = this._buildMetadata(sessionData, externalData);
		}

		this.load(settings.SegmentKey);

		window.accountInfo = accountInfo;

		setTimeout(() => {
			this._setSegmentAnalyticsAgent();
			this._identify(this.metadata);
		}, 3000);
	};

	// use this to ensure that initLogRocket is only called once.
	static _logRocketInitPromise = null;

	/**
	 * Initialize LogRocket if not disabled by LaunchDarkly flag, race-safe.
	 * @param {object} ldClient - LaunchDarkly client instance
	 * @param {object} accountInfo - User account info for identification
	 */
	static async initLogRocket(ldClient, accountInfo = {}) {
		const { settings = {} } = Session.getData();
		try {
			if (this._logRocketInitPromise) return this._logRocketInitPromise;

			if (
				!ldClient ||
				!ldClient.variation ||
				typeof ldClient.variation !== 'function'
			) {
				this._logRocketInitPromise = undefined;
				return undefined;
			} else {
				this._logRocketInitPromise = (async () => {
					const logrocketDisabled = await ldClient.variation(
						'logrocket-disabled',
						false
					);
					const logrocketOverrideAppId = await ldClient.variation(
						'logrocket-override-appid',
						null
					);
					const logrocketRootHostname = await ldClient.variation(
						'logrocket-root-hostname',
						null
					);
					const logrocketEnabled = !logrocketDisabled;
					if (!logrocketEnabled) return;
					const appId = logrocketOverrideAppId || settings?.LogRocketAppID;
					LogRocket.init(appId, {
						rootHostname:
							logrocketRootHostname ||
							Analytics.getRootDomain(window.location.hostname),
					});
					if (accountInfo && accountInfo.userId) {
						const accountId = accountInfo.accountId.toString();
						const traits = {};
						if (
							typeof accountInfo.customerDetails === 'object' &&
							accountInfo.customerDetails
						) {
							traits.username = accountInfo.customerDetails.username;
							traits.email = accountInfo.customerDetails.email;
							traits.customerid = accountInfo.customerId;
						}
						LogRocket.identify(accountInfo.userId, traits);
					}
				})();
				return this._logRocketInitPromise;
			}
		} catch (e) {
			console.log('Error initializing LogRocket:', e);
			return undefined;
		}
	}

	static getRootDomain(hostname) {
		// Case 2: qa[\d+].stamps.dev or qa[\d+].stamps.com
		let match = hostname.match(/(qa\d+\.stamps\.(dev|com))$/);
		if (match) return match[1];

		// Case 3: \w+.corp.stamps.com
		match = hostname.match(/((\w+)\.corp\.stamps\.com)$/);
		if (match) return match[1];

		// Case 1: stamps.com
		match = hostname.match(/([^.]+\.stamps\.com)$/);
		if (match) return match[1];

		// Fallback: return the hostname as is
		return hostname;
	}
}
